// src/services/api/axiosInstance.js
import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clearTokens,
} from "../utils/authUtils";

const API_BASE_URL =
  import.meta.env.VITE_APP_API_BASE_URL || "http://localhost:8080/api/v1";

const PUBLIC_ENDPOINTS = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh-token",
  "/auth/verify",
  "/auth/resend-otp",
  "/auth/request-password-reset",
  "/auth/reset-password",
  "/auth/activate-account",
  "/auth/request-activate-account",
  "/auth/token-valid",
  "/auth/verify-pin",
  "/auth/pin/setup",
  "/auth/pin/forgot",
  "/auth/pin/reset",
];

const isPublicEndpoint = (url = "") =>
  PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    error ? promise.reject(error) : promise.resolve(token);
  });
  failedQueue = [];
};

const redirectToLogin = () => {
  clearTokens();
  globalThis.location.replace("/auth/login");
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token && !isPublicEndpoint(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,

  async (error) => {
    if (!error.response || !error.config) {
      throw error;
    }

    const { status } = error.response;
    const originalRequest = error.config;

    if (status !== 401) throw error;

    if (isPublicEndpoint(originalRequest.url)) throw error;

    if (originalRequest._retry) throw error;

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      });
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      redirectToLogin();
      throw error;
    }

    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/refresh-token`,
        {},
        { headers: { Authorization: `Bearer ${refreshToken}` } },
      );

      const newAccessToken = data.accessToken;
      const newRefreshToken = data.refreshToken;

      if (!newAccessToken) {
        // Backend returned 200 but payload is malformed — treat as failure.
        throw new Error("Refresh succeeded but response contained no access token.");
      }

      setAccessToken(newAccessToken);
      if (newRefreshToken) setRefreshToken(newRefreshToken);

      processQueue(null, newAccessToken);

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      redirectToLogin();
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  },
);

export default axiosInstance;
